import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../services/api.service';
import Layout from '../components/Layout';
import { BellIcon, CheckCircleIcon, XCircleIcon, ExclamationCircleIcon, CalendarIcon, DocumentTextIcon, CurrencyDollarIcon, ScaleIcon, TrashIcon } from '@heroicons/react/24/outline';
import logger from '../utils/logger';

const NotificationsPage = () => {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, read, unread

    useEffect(() => {
        fetchNotifications();
    }, []);

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await notificationService.getNotifications();
            setNotifications(response.notifications || []);
        } catch (error) {
            logger.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const markAllAsRead = async () => {
        try {
            await notificationService.markAllAsRead();
            setNotifications(notifications.map(n => ({ ...n, is_read: true })));
        } catch (error) {
            logger.error('Error marking all notifications as read:', error);
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            await notificationService.markAsRead(notificationId);
            setNotifications(notifications.map(n =>
                n.id === notificationId ? { ...n, is_read: true } : n
            ));
        } catch (error) {
            logger.error('Error marking notification as read:', error);
        }
    };

    const deleteNotification = async (notificationId) => {
        if (window.confirm('هل أنت متأكد من حذف هذا الإشعار؟')) {
            try {
                await notificationService.deleteNotification(notificationId);
                setNotifications(notifications.filter(n => n.id !== notificationId));
            } catch (error) {
                logger.error('Error deleting notification:', error);
            }
        }
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'CASE_UPDATE': return <ScaleIcon className="h-6 w-6 text-blue-500" />;
            case 'SESSION': return <CalendarIcon className="h-6 w-6 text-green-500" />;
            case 'PAYMENT': return <CurrencyDollarIcon className="h-6 w-6 text-yellow-500" />;
            case 'DOCUMENT': return <DocumentTextIcon className="h-6 w-6 text-purple-500" />;
            case 'warning': return <ExclamationCircleIcon className="h-6 w-6 text-red-500" />;
            case 'info': return <BellIcon className="h-6 w-6 text-blue-400" />;
            default: return <BellIcon className="h-6 w-6 text-gray-500" />;
        }
    };

    const getTimeAgo = (date) => {
        const now = new Date();
        const diff = now - new Date(date);
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) return `منذ ${days} يوم${days > 1 ? 'اً' : ''}`;
        if (hours > 0) return `منذ ${hours} ساعة`;
        if (minutes > 0) return `منذ ${minutes} دقيقة`;
        return 'الآن';
    };

    const filteredNotifications = notifications.filter(notification => {
        if (filter === 'read') return notification.is_read;
        if (filter === 'unread') return !notification.is_read;
        return true;
    });

    return (
        <Layout>
            <div className="container mx-auto px-4 py-8" dir="rtl">
                <div className="mb-6 md:flex md:items-center md:justify-between">
                    <div className="flex-1 min-w-0">
                        <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">الإشعارات</h2>
                    </div>
                    <div className="mt-4 flex md:mt-0 md:ml-4">
                        <button
                            onClick={markAllAsRead}
                            type="button"
                            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            تحديد الكل كمقروء
                        </button>
                    </div>
                </div>
                
                <div className="bg-white shadow rounded-lg mb-6 p-4">
                    <div className="flex flex-wrap gap-2">
                        <button
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                filter === 'all' 
                                    ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                            onClick={() => setFilter('all')}
                        >
                            <span className="flex items-center">
                                <BellIcon className="h-5 w-5 ml-2" />
                                الكل ({notifications.length})
                            </span>
                        </button>
                        <button
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                filter === 'unread' 
                                    ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                            onClick={() => setFilter('unread')}
                        >
                            <span className="flex items-center">
                                <ExclamationCircleIcon className="h-5 w-5 ml-2" />
                                غير مقروء ({notifications.filter(n => !n.is_read).length})
                            </span>
                        </button>
                        <button
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                filter === 'read' 
                                    ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                            onClick={() => setFilter('read')}
                        >
                            <span className="flex items-center">
                                <CheckCircleIcon className="h-5 w-5 ml-2" />
                                مقروء ({notifications.filter(n => n.is_read).length})
                            </span>
                        </button>
                    </div>
                </div>

            {loading ? (
                <div className="bg-white shadow overflow-hidden sm:rounded-md p-12 text-center">
                    <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                    <p className="mt-4 text-gray-600">جاري تحميل الإشعارات...</p>
                </div>
            ) : filteredNotifications.length === 0 ? (
                <div className="bg-white shadow overflow-hidden sm:rounded-md p-12 text-center">
                    <div className="flex justify-center">
                        <BellIcon className="h-16 w-16 text-gray-400" />
                    </div>
                    <h3 className="mt-4 text-lg font-medium text-gray-900">لا توجد إشعارات {filter === 'unread' ? 'غير مقروءة' : filter === 'read' ? 'مقروءة' : ''}</h3>
                    <p className="mt-2 text-sm text-gray-500">{filter === 'unread' ? 'جميع الإشعارات تمت قراءتها' : filter === 'read' ? 'لا توجد إشعارات مقروءة بعد' : 'لم يتم تسجيل أي إشعارات بعد'}</p>
                    {filter !== 'all' && (
                        <div className="mt-6">
                            <button
                                onClick={() => setFilter('all')}
                                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                عرض جميع الإشعارات
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                    <ul className="divide-y divide-gray-200">
                        {filteredNotifications.map(notification => (
                            <li key={notification.id}>
                                    <div
                                        onClick={() => {
                                            if (!notification.is_read) markAsRead(notification.id);
                                            if (notification.link) {
                                                navigate(notification.link);
                                            }
                                        }}
                                        className={`
                                            block hover:bg-gray-50 cursor-pointer transition-colors
                                            ${!notification.is_read ? 'bg-blue-50' : ''}
                                        `}
                                    >
                                    <div className="px-4 py-4 sm:px-6">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 ml-3">
                                                    {getNotificationIcon(notification.type)}
                                                </div>
                                                <div className="mr-4">
                                                    <h4 className="text-lg font-medium text-gray-900">
                                                        {notification.title}
                                                    </h4>
                                                    <p className="mt-1 text-sm text-gray-600">
                                                        {notification.message || notification.event_title || notification.title}
                                                    </p>
                                                    {notification.description && (
                                                        <p className="mt-2 text-sm text-gray-500">
                                                            {notification.description}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-end space-y-2">
                                                <div className="flex items-center space-x-2 space-x-reverse">
                                                    <span className="text-sm text-gray-500">
                                                        {getTimeAgo(notification.created_at)}
                                                    </span>
                                                    {!notification.is_read && (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                            جديد
                                                        </span>
                                                    )}
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); deleteNotification(notification.id); }}
                                                        className="text-gray-400 hover:text-red-500 transition-colors"
                                                    >
                                                        <TrashIcon className="h-5 w-5" />
                                                    </button>
                                                </div>
                                                {notification.date && (
                                                    <div className="flex items-center text-sm text-gray-500">
                                                        <CalendarIcon className="h-4 w-4 ml-1" />
                                                        {format(new Date(notification.date), 'dd MMMM yyyy', { locale: ar })}
                                                        {notification.time && (
                                                            <span className="mr-2">الساعة {notification.time}</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            </div>
        </Layout>
    );
};

export default NotificationsPage;
